<template>
  <q-page padding v-if="ready">
    <!-- content -->
    <q-banner v-if="isReportLocked" rounded class="bg-info text-white q-mb-md">
      <template v-slot:avatar>
        <q-icon name="cloud_upload" />
      </template>
      {{ $t('report.lockedMessage') }}
    </q-banner>
    <q-field borderless v-if="isVacant">
      <template v-slot:before>
        <q-icon name="my_location" color="primary" />
      </template>
      <template v-slot:control>
        <sw-filterable-select
          v-model="selectedSpot"
          :label="$t('report.housing')"
          :options="spotsOptions"
          :disable="isReportLocked"
        />
      </template>
    </q-field>
    <div v-if="isCheckpointsEmpty">
      <div class="text-center absolute-center full-width text-grey-7 q-pb-xl">
        <div>{{ $t('report.reloadAuditMessage') }}</div>
        <q-btn color="primary" @click="reloadAudit">{{
          $t('common.reload')
        }}</q-btn>
      </div>
    </div>
    <template v-if="showCheckpointsList && !isCheckpointsEmpty">
      <template v-for="checkpoint in computedCheckpoints" :key="checkpoint.id">
        <div :ref="`${checkpoint.id}`">
          <div class="row text-subtitle1 text-grey-6 q-pt-md">
            <q-icon
              v-if="hasDetails(checkpoint)"
              name="info_outline"
              size="20px"
              class="q-mr-xs q-pt-sm"
              color="primary"
              @click="showDetails(checkpoint)"
            />
            {{ checkpoint.question }}
          </div>
          <div class="row">
            <div class="col-xs-5 q-px-sm">
              <q-btn
                class="full-width"
                dense
                :outline="checkpoint.asserted !== true"
                :disable="
                  isReportLocked ||
                  checkpoint.asserted === true ||
                  isIssuePresent(checkpoint.id) != null
                "
                :color="checkpoint.asserted === true ? 'positive' : 'grey-7'"
                @click="toggleAsserted(checkpoint, true)"
              >
                <span v-if="checkpoint.asserted === true"
                  ><q-icon name="assignment_turned_in"
                /></span>
                <span v-else>Ok</span>
              </q-btn>
            </div>
            <div class="col-xs-5 q-px-sm">
              <q-btn
                class="full-width"
                dense
                :outline="!isIssuePresent(checkpoint.id)"
                :disable="isReportLocked"
                :color="
                  checkpoint.asserted === false && isIssuePresent(checkpoint.id)
                    ? 'negative'
                    : 'grey-7'
                "
                @click="toggleAsserted(checkpoint, false)"
              >
                <span v-if="isIssuePresent(checkpoint.id)"
                  ><q-icon name="assignment_late"
                /></span>
                <span v-else>{{ $t('report.ko') }}</span>
              </q-btn>
            </div>
            <div class="col-xs-2 q-px-sm">
              <q-btn
                class="full-width"
                dense
                :outline="checkpoint.missing !== true"
                :disable="isReportLocked || isIssuePresent(checkpoint.id) != null"
                color="warning"
                @click="toggleMissing(checkpoint, true)"
              >
                <span v-if="checkpoint.missing === true"
                  ><q-icon name="visibility_off"
                /></span>
                <span v-else><q-icon name="clear" /></span>
              </q-btn>
            </div>
          </div>
        </div>
      </template>
    </template>
    <div v-if="!isCheckpointsEmpty" class="row q-pt-md">
      <div class="col text-center">
        <q-btn
          small
          color="primary"
          :disable="isLoading"
          @click="sendReport"
          v-if="reportComplete && !isReportLocked"
        >
          <q-icon left size="sm" name="archive" />
          {{ $t('common.archive') }}
          <q-spinner size="20" color="white" v-if="isLoading" />
        </q-btn>
      </div>
    </div>
    <q-dialog v-model="dialogOpened">
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ $t('report.accuracy') }} :</div>
        </q-card-section>
        <q-card-section>
          <span v-html="currentCheckpoint.description" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            flat
            label="OK"
            color="primary"
            @click="dialogOpened = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-model="isMissingToggled" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-subtitle1 text-bold">
            {{ $t('report.excludedCheckpoint') }}
          </div>
          <div class="text-subtitle1">
            {{ $t('report.excludedCheckpointWarning') }}
          </div>
          <div class="text-subtitle1 text-bold q-mt-sm">
            {{ $t('report.comment.label') }} :
          </div>
          <q-input
            :placeholder="$t('report.comment.placeholder')"
            dense
            type="textarea"
            outlined
            v-model="comment"
          />
        </q-card-section>

        <q-card-actions align="right" class="text-primary">
          <q-btn
            flat
            :label="$t('common.cancel')"
            @click="comment = ''"
            v-close-popup
          />
          <q-btn flat label="OK" v-close-popup @click="setMissingCheckpoint" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
const uuidv1 = require('uuid/v1')
import { scroll } from 'quasar'
const { getScrollTarget, setVerticalScrollPosition } = scroll
import eventBus from 'src/eventBus'
import SwFilterableSelect from 'src/components/inputs/sw-filterable-select.vue'
import { normalizeReport, computeAsserted } from 'src/utils/normalizeReport'
import {
  rejectExcludedCheckpoints,
  pruneDraftCheckpoints
} from 'src/utils/checkpoints'
import { findTopCategoryId } from 'src/utils/categories'
import moment from 'moment'

export default {
  name: 'Checklist',
  inject: ['$helpers'],
  components: { SwFilterableSelect },
  data() {
    return {
      ready: false,
      dialogOpened: false,
      currentCheckpoint: {},
      report: {
        _id: 0,
        audit: { id: 0 },
        checkpoints: [],
        issues: [],
        status: ''
      },
      audit: {
        id: this.$route.params.auditId,
        checkpoints: [],
        checklist: { checkpoints: [] }
      },
      isLoading: false,
      selectedSpot: { label: '', value: '' },
      spots: [],
      isMissingToggled: false,
      checkpointSelected: undefined,
      comment: undefined
    }
  },
  computed: {
    ...mapGetters({
      auditsInOtherScopes: 'audits/auditsInOtherScopes',
      categories: 'company/categories',
      storedSpotId: 'report/storedSpotId',
      checkedReports: 'report/checkedReports',
      places: 'reporter/places',
      company: 'company/attributes',
      qrCode: 'reporter/qrCode'
    }),
    isCheckpointsDefaultOk() {
      return !!this.company.isCheckpointsDefaultOk
    },
    // NOTE: la copie locale de l'audit peut avoir perdu ses checkpoints
    // (doc PouchDB corrompu) — on ne lit jamais la clé sans garde-fou
    auditCheckpoints() {
      return Array.isArray(this.audit.checkpoints) ? this.audit.checkpoints : []
    },
    computedCheckpoints() {
      if (!this.report.checkpoints || !this.report.checkpoints.length) {
        return []
      }
      return this.report.checkpoints.map((checkpoint) => {
        const isIssuePresent = this.isIssuePresent(checkpoint.id)
        checkpoint.asserted = computeAsserted(
          checkpoint.asserted,
          checkpoint.missing,
          isIssuePresent
        )
        return checkpoint
      })
    },
    reportComplete() {
      let hasMissingCheckpoints = this.computedCheckpoints.filter(
        (el) =>
          (el.asserted === null && el.missing === null) ||
          (el.asserted === false && !this.isIssuePresent(el.id))
      )
      return hasMissingCheckpoints.length === 0
    },
    isCheckpointsEmpty() {
      // NOTE: la liste affichée vient du brouillon (report.checkpoints) mais les
      // libellés/détails viennent de l'audit — si l'audit local a perdu ses
      // checkpoints, on propose la recharge au lieu de rendre une liste dégradée
      return !this.auditCheckpoints.length || !this.computedCheckpoints.length
    },
    isVacant() {
      return Boolean(this.$route.query.vacant)
    },
    isReportLocked() {
      // NOTE: the report is queued for sync, editing it would conflict
      // with the sync job and could produce duplicates
      return ['PENDING', 'IN_PROGRESS', 'FAILED'].includes(this.report.status)
    },
    showCheckpointsList() {
      if (!this.isVacant) return true
      return !!this.selectedSpotId // NOTE: syntaxe pour tester si selectedSpotId est null OU undefined
    },
    spotsOptions() {
      const sortedSpot = this.sortList(
        this.$helpers.resourcesToFormOptions(this.spots)
      )
      sortedSpot.map((spot) => {
        return {
          value: Number(spot.id),
          label: spot.name
        }
      })
      return sortedSpot
    },
    selectedSpotId() {
      return this.selectedSpot?.value
    },
    topCategoryId() {
      return findTopCategoryId(
        this.categories,
        this.audit?.checklist?.category?.id
      )
    }
  },
  watch: {
    selectedSpotId(value) {
      if (value) {
        this.$store.dispatch('report/setStoredSpotId', value)
        const spot = this.spotsOptions.find(
          (s) => Number(s.value) === Number(value)
        )
        this.selectedSpot = spot
      }
    },
    showCheckpointsList(value) {
      if (value && this.checkedReports) {
        let checkedReport = this.checkedReports.find(
          (el) => el.auditId === Number(this.$route.params.auditId)
        )
        if (checkedReport !== undefined) {
          setTimeout(() => {
            this.scrollToElement(checkedReport.id)
          }, 500)
        }
      }
    }
  },
  methods: {
    scrollToElement(el) {
      const element = this.$refs[el][0]
      const target = getScrollTarget(element)
      const offset = element.offsetTop
      const duration = 300
      setTimeout(() => {
        element.style.backgroundColor = 'rgba(64, 196, 255, 0.15)'
      }, 400)
      setTimeout(() => {
        element.style.backgroundColor = 'white'
      }, 1000)
      setVerticalScrollPosition(target, offset, duration)
    },
    isIssuePresent(checkpointId) {
      return this.report.issues.find(
        (issue) => Number(issue.checkpoint.id) === Number(checkpointId)
      )
    },
    async setAudit() {
      let audit
      if (this.$route.query.tab === 'myScope') {
        audit = await this.$patrimoine
          .auditsCollection()
          .get(this.$route.params.auditId)
      } else {
        audit = this.auditsInOtherScopes.find(
          (audit) => audit.id === Math.trunc(this.$route.params.auditId)
        )
      }

      if (audit) {
        this.audit = audit
        this.$emit('title', audit.checklist.name)
      } else {
        await this.$SowellProxy
          .loadCheckpoints(this.audit.id)
          .then(({ checkpoints, excludedCheckpoints }) => {
            // NOTE: findOrCreateReport et hasDetails lisent les checkpoints
            // sous cette clé
            // NOTE: FEAT-4256 — même croisement que le store : la liste vient
            // du template de checklist, les exclusions de l'audit
            this.audit.checkpoints = rejectExcludedCheckpoints(
              checkpoints,
              excludedCheckpoints
            )
          })
      }
    },

    //create pouchDB local report if not exist
    async findOrCreateReport() {
      let self = this
      await self.$DB
        .tmpReports()
        .find({
          selector: {
            // NOTE: match both types, legacy docs may hold a string audit id
            'audit.id': {
              $in: [Number(self.audit.id), String(self.audit.id)]
            },
            status: {
              $in: ['ONGOING', 'PENDING', 'FAILED', 'IN_PROGRESS']
            }
          }
        })
        .then(async (results) => {
          if (results.docs.length) {
            self.report = results.docs[0]
            await self.pruneExcludedFromDraft()
          } else {
            const checkpoints = self.audit.checkpoints
              ? self.audit.checkpoints.map((element) => {
                  return {
                    id: element.id,
                    question: element.question,
                    asserted: this.isCheckpointsDefaultOk ? true : null,
                    missing: null
                  }
                })
              : []
            if (!checkpoints.length) {
              throw 'Audit without checkpoint'
            }

            let report = {
              _id: uuidv1(),
              remoteId: '',
              audit: { id: Number(self.audit.id) },
              checkpoints,
              issues: [],
              status: 'ONGOING'
            }
            await self.$DB
              .tmpReports()
              .put(report)
              .then(async () => {
                await self.findOrCreateReport()
                // eslint-disable-next-line handle-callback-err
              })
              .catch(function (err) {
                console.error(err)
              })
          }
        })
    },
    // NOTE: FEAT-4256 — un brouillon créé avant le correctif porte encore les
    // checkpoints désormais exclus, et reste inarchivable tant qu'on n'y a pas
    // répondu. On ne touche qu'aux brouillons ONGOING : les statuts verrouillés
    // portent des réponses déjà en file de synchro et le metadata.uid qui sert
    // à la détection de doublon côté serveur.
    async pruneExcludedFromDraft() {
      if (this.report.status !== 'ONGOING') return
      // NOTE: un audit local ayant perdu ses checkpoints (doc PouchDB corrompu)
      // ne doit surtout pas vider le brouillon : isCheckpointsEmpty proposera la
      // recharge, qui est la bonne sortie
      if (!this.auditCheckpoints.length) return
      const pruned = pruneDraftCheckpoints(
        this.report,
        this.auditCheckpoints.map((checkpoint) => checkpoint.id)
      )
      if (!pruned) return
      await this.$DB.tmpReports().put(pruned)
      await this.findOrCreateReport()
    },
    toggleAsserted(checkpoint, newState) {
      if (this.isReportLocked) return
      const storedCheckpoint = this.report.checkpoints.find(
        (c) => c.id === checkpoint.id
      )
      if (!storedCheckpoint) {
        console.error('Cannot find checkpoint', checkpoint)
        return
      }
      let checkedReport = {
        id: Number(checkpoint.id),
        auditId: Number(this.$route.params.auditId)
      }
      this.$store.dispatch('report/setcheckedReport', checkedReport)
      if (newState !== null) {
        if (newState === false) {
          this.$router.push({
            path:
              '/zones/' +
              this.$route.params.zoneId +
              '/places/' +
              this.$route.params.placeId +
              '/audits/' +
              this.audit.id +
              '/issue',
            query: {
              checkpoint: checkpoint.id,
              report: this.report._id,
              tab: this.$route.query.tab ? this.$route.query.tab : 'myScope',
              spot: this.selectedSpotId
            }
          })
        } else {
          storedCheckpoint.asserted = newState
          this.toggleMissing(checkpoint, null)
          this.$DB
            .tmpReports()
            .put(this.report)
            .then(async () => {
              await this.findOrCreateReport()
            })
        }
      }
    },
    toggleMissing(checkpoint, newState) {
      if (this.isReportLocked) return
      const storedCheckpoint = this.report.checkpoints.find(
        (c) => c.id === checkpoint.id
      )
      if (storedCheckpoint.comment) {
        this.comment = storedCheckpoint.comment
      }
      if (newState !== null) {
        this.checkpointSelected = {
          checkpoint,
          newState
        }
        this.isMissingToggled = true
      } else {
        if (!storedCheckpoint) {
          console.error('Cannot find checkpoint', checkpoint)
          return
        }
        storedCheckpoint.missing = newState
        delete storedCheckpoint.comment
        this.comment = ''
      }
    },
    setMissingCheckpoint() {
      if (this.checkpointSelected) {
        const { checkpoint, newState } = this.checkpointSelected
        const storedCheckpoint = this.report.checkpoints.find(
          (c) => c.id === checkpoint.id
        )
        if (!storedCheckpoint) {
          return
        }
        storedCheckpoint.missing = newState
        if (this.comment !== '') {
          storedCheckpoint.comment = this.comment
        }
        if (newState !== null) {
          this.toggleAsserted(checkpoint, null)
          this.$DB
            .tmpReports()
            .put(this.report)
            .then(async () => {
              await this.findOrCreateReport()
              this.checkpointSelected = undefined
            })
        }
        this.isMissingToggled = false
        this.comment = ''
      } else {
        console.error('Cannot find checkpoint')
        this.isMissingToggled = false
      }
    },
    sendReport() {
      if (this.isLoading) return
      if (['PENDING', 'IN_PROGRESS'].includes(this.report.status)) {
        this.$router.push({
          path: '/zones',
          query: {
            tab: this.$route.query.tab ? this.$route.query.tab : 'myScope'
          }
        })
        return
      }
      if (this.isVacant) {
        if (!this.selectedSpotId) {
          // NOTE: this should never happen
          this.$q.notify({
            message: this.$t('report.missingSpotError'),
            color: 'warning',
            position: 'top'
          })
          return
        }
        this.report.spotId = this.selectedSpotId
      }
      const previousStatus = this.report.status
      this.report.status = 'PENDING'
      this.isLoading = true

      let normalizedReport
      let issuesToStore
      try {
        normalizedReport = normalizeReport(this.report)

        if (!normalizedReport) {
          console.warn(
            'error during normalizeReport, sending non normalizedReport'
          )
          normalizedReport = this.report
        }

        normalizedReport.scheduledAt = moment().format('YYYY/MM/DD HH:mm')

        normalizedReport.audit = {
          id: Number(this.audit.id),
          place: { id: this.audit.place.id, name: this.audit.place.name },
          checklist: {
            id: this.audit.checklist.id,
            name: this.audit.checklist.name,
            category: {
              id: this.audit.checklist.category.id,
              name: this.audit.checklist.category.name,
              img: this.audit.checklist.category.img
            }
          }
        }
        issuesToStore = normalizedReport.issues
        delete normalizedReport.issues
      } catch (err) {
        console.error('sendReport preparation failed', err)
        this.report.status = previousStatus
        this.isLoading = false
        this.$q.notify({
          message: this.$t('report.sendReportError'),
          color: 'warning',
          position: 'top'
        })
        return
      }

      this.$DB
        .tmpReports()
        .put(normalizedReport)
        .then((storedReport) => {
          issuesToStore = issuesToStore.map((issueToStore) => {
            issueToStore.report = {
              id: null,
              localId: storedReport.id
            }
            return issueToStore
          })
          this.$DB.tmpIssues().bulkDocs(issuesToStore)
        })
        .then(async () => {
          this.$store.dispatch(
            'report/reset',
            Number(this.$route.params.auditId)
          )
          this.$store.dispatch('audits/createNotification', true)
          eventBus.$emit('processQueue')
          await this.$store.dispatch('audits/reload')
        })
        .catch(async (err) => {
          console.error('sendReport', err)
          this.$q.notify({
            message: this.$t('report.sendReportError'),
            color: 'warning',
            position: 'top'
          })
        })
        .finally(() => {
          this.$store.dispatch('report/setStoredSpotId', null)
          if (this.qrCode) {
            this.$router.go(-1)
            return
          }
          this.$router.push({
            path: '/zones',
            query: {
              tab: this.$route.query.tab ? this.$route.query.tab : 'myScope'
            }
          })
          this.isLoading = false
        })
    },
    hasDetails(checkpoint) {
      const currentCheckpoint = this.auditCheckpoints.find(
        (el) => el.id === checkpoint.id
      )
      return Boolean(
        currentCheckpoint &&
          'description' in currentCheckpoint &&
          currentCheckpoint.description !== null &&
          currentCheckpoint.description.length
      )
    },
    showDetails(checkpoint) {
      this.currentCheckpoint =
        this.auditCheckpoints.find((el) => el.id === checkpoint.id) || {}
      this.dialogOpened = true
    },
    sortList(tabOptions) {
      return tabOptions.sort((a, b) => {
        if (a.label === null) {
          return true
        } else {
          return String(a.label).localeCompare(String(b.label), 'fr', {
            ignorePunctuation: true,
            numeric: true,
            sensitivity: 'base'
          })
        }
      })
    },

    loadSpots() {
      this.$q.loading.show()
      return this.$SowellProxy
        .loadHousingRelatedSpots(this.$route.params.placeId, this.topCategoryId)
        .then((spots) => {
          this.spots = spots
          this.$q.loading.hide()
        })
    },
    selectInputHeight(options) {
      return options.length > 3 ? { height: '150px' } : undefined
    },
    onPopupHide(ref) {
      this.$refs[`${ref}`].blur()
    },
    async reloadAudit() {
      this.$q.loading.show()
      const auditId = Number(this.$route.params.auditId)
      try {
        await this.$store.dispatch('audits/reloadAudit', auditId)
        await this.$DB
          .tmpReports()
          .find({
            selector: {
              'audit.id': {
                $in: [Number(auditId), String(auditId)]
              },
              // NOTE: only drafts can be discarded — PENDING/IN_PROGRESS/FAILED
              // reports hold unsent answers and the uid used by the server-side
              // duplicate detection
              status: 'ONGOING'
            }
          })
          .then(async (result) => {
            const brokenDrafts = result.docs.filter(
              (doc) => !doc.checkpoints || !doc.checkpoints.length
            )
            await Promise.all(
              brokenDrafts.map((doc) => this.$DB.tmpReports().remove(doc))
            )
          })
        await this.setAudit()
        await this.findOrCreateReport()
      } catch (error) {
        console.error('reloadAudit error:', error)
        this.$q.notify({
          message: this.$t('report.reloadAuditError'),
          color: 'warning',
          position: 'top'
        })
      } finally {
        this.$q.loading.hide()
      }
    }
  },

  async mounted() {
    try {
      await this.setAudit()
      await this.findOrCreateReport()

      if (this.isReportLocked) {
        // NOTE: make sure the sync job is running for this queued report
        eventBus.$emit('processQueue')
      }

      if (this.isVacant) {
        await this.loadSpots()
        if (this.storedSpotId) {
          this.$nextTick(() => {
            const storedSpot = this.spots.find(
              (spot) => spot.id === this.storedSpotId
            )
            this.selectedSpot = { label: storedSpot.name, value: storedSpot.id }
            this.$store.dispatch('report/setStoredSpotId', this.storedSpotId)
          })
        } else {
          this.selectedSpot = { label: '', value: '' }
          this.$store.dispatch('report/setStoredSpotId', null)
        }
      } else {
        this.selectedSpot = { label: '', value: '' }
        this.$store.dispatch('report/setStoredSpotId', null)
      }
    } catch (error) {
      console.error('reloadAudit error:', error)
      this.$q.notify({
        message: this.$t('report.reloadAuditError'),
        color: 'warning',
        position: 'top'
      })
    } finally {
      this.$q.loading.hide()
      this.ready = true
    }

    if (this.checkedReports && (!this.isVacant || !!this.selectedSpotId)) {
      let checkedReport = this.checkedReports.find(
        (el) => el.auditId === Number(this.$route.params.auditId)
      )
      if (checkedReport !== undefined) {
        setTimeout(() => {
          this.scrollToElement(checkedReport.id)
        }, 500)
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.q-btn {
  margin: 5px;
}

.btn-fixed-width {
  max-width: 300px;
  width: 40%;
  min-width: 150px;
}

.modal-body {
  margin-top: 15px;
}

.qSelect {
  width: 100%;
}
</style>
